/**
 * PinnedModules Component
 * Displays pinned/favorite modules at the top of the sidebar for quick access
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Pin, X } from 'lucide-react';
import { usePinnedModules } from '../../hooks/usePinnedModules';
import { withTranslation } from 'react-i18next';

interface PinnedModulesProps {
  t: (key: string) => string;
}

const PinnedModules: React.FC<PinnedModulesProps> = ({ t }) => {
  const { pinnedModules, unpinModuleAction } = usePinnedModules();
  const location = useLocation();

  if (pinnedModules.length === 0) {
    return null;
  }

  const isActiveLink = (link: string) => {
    if (!link || link === '#') return false;
    return location.pathname === link || location.pathname.startsWith(link + '/');
  };

  return (
    <div className="mx-3 mb-3 group-data-[sidebar-size=sm]:hidden group-data-[layout=horizontal]:hidden">
      {/* Container with background and border */}
      <div className="bg-slate-50/80 dark:bg-zink-700/50 rounded-lg border border-slate-200/80 dark:border-zink-600 group-data-[sidebar=dark]:bg-zink-700/50 group-data-[sidebar=dark]:border-zink-600 group-data-[sidebar=brand]:bg-white/10 group-data-[sidebar=brand]:border-white/20 group-data-[sidebar=modern]:bg-slate-100/50 group-data-[sidebar=modern]:border-slate-200/50">
        {/* Section Header */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200/60 dark:border-zink-600/60 group-data-[sidebar=dark]:border-zink-600/60 group-data-[sidebar=brand]:border-white/10 group-data-[sidebar=modern]:border-slate-200/40">
          <div className="flex items-center justify-center w-5 h-5 rounded bg-custom-500/10 dark:bg-custom-500/20 group-data-[sidebar=brand]:bg-white/20">
            <Pin className="h-3 w-3 text-custom-500 dark:text-custom-400 group-data-[sidebar=brand]:text-white" />
          </div>
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 dark:text-zink-300 group-data-[sidebar=dark]:text-zink-300 group-data-[sidebar=brand]:text-white/90 group-data-[sidebar=modern]:text-slate-600">
            {t('Quick Access')}
          </span>
          <span className="ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-200/80 dark:bg-zink-600 text-slate-500 dark:text-zink-400 group-data-[sidebar=dark]:bg-zink-600 group-data-[sidebar=dark]:text-zink-400 group-data-[sidebar=brand]:bg-white/20 group-data-[sidebar=brand]:text-white/80">
            {pinnedModules.length}
          </span>
        </div>

        {/* Pinned Items */}
        <ul className="p-1.5 space-y-0.5">
          {pinnedModules.map((module) => (
            <li key={module.id} className="group/pinned relative">
              <Link
                to={module.link || '#'}
                className={`flex flex-col px-2.5 py-2 text-[13px] rounded-md transition-all duration-150
                  ${isActiveLink(module.link)
                    ? 'bg-custom-500 text-white shadow-sm'
                    : 'text-slate-700 hover:bg-white hover:shadow-sm dark:text-zink-200 dark:hover:bg-zink-600 group-data-[sidebar=dark]:text-zink-200 group-data-[sidebar=dark]:hover:bg-zink-600 group-data-[sidebar=brand]:text-white/90 group-data-[sidebar=brand]:hover:bg-white/10 group-data-[sidebar=modern]:text-slate-700 group-data-[sidebar=modern]:hover:bg-white'
                  }`}
              >
                {/* Main row with label and unpin button */}
                <div className="flex items-center justify-between w-full">
                  <span className="truncate flex-1 font-medium">{t(module.label)}</span>

                  {/* Unpin button - shows on hover */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      unpinModuleAction(module.id);
                    }}
                    className={`opacity-0 group-hover/pinned:opacity-100 p-1 rounded-md transition-all ml-2 flex-shrink-0 ${
                      isActiveLink(module.link)
                        ? 'hover:bg-white/20 text-white/80 hover:text-white'
                        : 'hover:bg-red-100 text-slate-400 hover:text-red-500 dark:hover:bg-red-500/20 dark:text-zink-400 dark:hover:text-red-400'
                    }`}
                    title={t('Unpin')}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* Parent label badge - shows if exists */}
                {module.parentLabel && (
                  <span className={`text-[10px] mt-1 font-normal ${
                    isActiveLink(module.link)
                      ? 'text-white/70'
                      : 'text-slate-500 dark:text-zink-400 group-data-[sidebar=brand]:text-white/60'
                  }`}>
                    <span className="opacity-60">in</span> {t(module.parentLabel)}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default withTranslation()(PinnedModules);
